import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth } from '../../firebase/config';
import { collection, doc, getDoc, updateDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import Select from 'react-select';
import './CreateTasklist.css'; // We'll reuse the same styling
import { FaTrash, FaGripVertical } from 'react-icons/fa';

const fontSizes = {
  section: 18,
  subsection: 16,
  item: 14
};

function EditTasklist() {
  const [listName, setListName] = useState('');
  const [items, setItems] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { templateId } = useParams();

  // Fetch template and locations data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch template data
        const templateRef = doc(db, 'templates', templateId);
        const templateSnap = await getDoc(templateRef);
        
        if (!templateSnap.exists()) {
          console.error('Template not found');
          navigate('/forms');
          return;
        }

        const templateData = templateSnap.data();
        setListName(templateData.name);
        setItems(templateData.items);
        setSelectedLocations(templateData.assignedLocations || []);

        // Fetch locations
        const querySnapshot = await getDocs(collection(db, 'locations'));
        const locationData = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(loc => loc.status && loc.status.toLowerCase() === 'active');
        
        setLocations(locationData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (templateId) {
      fetchData();
    }
  }, [templateId, navigate]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(items);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);
    setItems(reorderedItems);
  };

  const addItem = (type) => {
    const newItem = {
      id: `item-${Date.now()}`,
      type,
      content: '',
      backgroundColor: type === 'section' 
        ? 'rgba(33, 150, 243, 0.15)' 
        : type === 'subsection'
        ? 'rgba(76, 175, 80, 0.15)'
        : 'rgba(255, 152, 0, 0.15)'
    };
    setItems(prev => [...prev, newItem]);
  };

  const validateTemplateData = () => {
    if (!listName.trim()) {
      alert('Please enter a template name');
      return false;
    }

    if (items.length === 0) {
      alert('Please add at least one item');
      return false;
    }

    if (selectedLocations.length === 0) {
      alert('Please select at least one location');
      return false;
    }

    // Validate that all items have content
    const emptyItems = items.filter(item => !item.content.trim());
    if (emptyItems.length > 0) {
      alert('Please fill in content for all items');
      return false;
    }

    return true;
  };

  const saveTemplate = async () => {
    if (!validateTemplateData()) return;
    
    const userId = user?.uid || auth.currentUser?.uid;
    if (!userId) {
      alert('You must be logged in to save templates');
      return;
    }
    
    setLoading(true);
    
    try {
      // Get user's document to fetch their name
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      
      // Get user email from either user object or userData
      const userEmail = user?.email || userData?.email || auth.currentUser?.email;
      
      if (!userEmail) {
        throw new Error('User email not found');
      }
      
      const templateRef = doc(db, 'templates', templateId);
      const templateData = {
        name: listName.trim(),
        items: items.map(item => ({
          id: item.id,
          type: item.type,
          content: item.content,
          isCompleted: false
        })),
        fontSizes,
        userId,
        lastModified: serverTimestamp(),
        isTemplate: true,
        assignedLocations: selectedLocations.map(loc => ({
          id: loc.id,
          name: loc.name
        })),
        lastModifiedBy: {
          id: userId,
          firstName: userData?.firstName || '',
          lastName: userData?.lastName || '',
          email: userEmail
        },
        lastModifiedAt: serverTimestamp(),
        status: 'active',
        templateVersion: 1
      };

      await updateDoc(templateRef, templateData);

      // Navigate back to the forms page with my templates tab active
      navigate('/forms', { 
        state: { 
          activeTab: 'my',
          message: 'Template updated successfully',
          type: 'success',
          scrollToTop: true
        }
      });
    } catch (error) {
      console.error('Error updating template:', error);
      alert('Error updating template: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="create-tasklist-container">
      <h1>Edit Template</h1>
      
      <div className="tasklist-header">
        <div className="form-section">
          <label>Template Name:</label>
          <input
            type="text"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            placeholder="Enter template name..."
            className="name-input"
          />
        </div>

        <div className="font-size-controls">
          {Object.entries(fontSizes).map(([type, size]) => (
            <div key={type} className="font-size-control">
              <label>{type.charAt(0).toUpperCase() + type.slice(1)} Font Size:</label>
              <input
                type="number"
                min="8"
                max="32"
                value={size}
                onChange={(e) => setFontSizes(prev => ({
                  ...prev,
                  [type]: parseInt(e.target.value) || prev[type]
                }))}
              />
              <span>px</span>
            </div>
          ))}
        </div>
      </div>

      <div className="tasklist-content">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tasklist-items">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="tasklist-items"
              >
                {items.map((item, index) => (
                  <Draggable 
                    key={item.id} 
                    draggableId={item.id} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`tasklist-item ${item.type} ${snapshot.isDragging ? 'is-dragging' : ''}`}
                      >
                        <div {...provided.dragHandleProps} className="drag-handle">
                          <FaGripVertical />
                        </div>
                        <input
                          type="text"
                          value={item.content}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setItems(prev => 
                              prev.map(i => 
                                i.id === item.id ? { ...i, content: newValue } : i
                              )
                            );
                          }}
                          onClick={e => e.stopPropagation()}
                          placeholder={`Enter ${item.type} text`}
                          className="tasklist-input"
                          style={{ fontSize: `${fontSizes[item.type]}px` }}
                        />
                        <button
                          onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))}
                          className="delete-btn-template"
                          type="button"
                          aria-label="Delete item"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <div className="add-buttons-container">
        <button 
          onClick={() => addItem('section')} 
          className="add-btn section"
          type="button"
        >
          Add Section
        </button>
        <button 
          onClick={() => addItem('subsection')} 
          className="add-btn subsection"
          type="button"
        >
          Add Subsection
        </button>
        <button 
          onClick={() => addItem('item')} 
          className="add-btn item"
          type="button"
        >
          Add Item
        </button>
      </div>

      <div className="location-selection">
        <div className="form-section">
          <label>Locations:</label>
          <Select
            isMulti
            value={selectedLocations.map(loc => ({
              value: loc.id,
              label: loc.name
            }))}
            onChange={(selectedOptions) => {
              const selected = selectedOptions ? selectedOptions.map(option => ({
                id: option.value,
                name: option.label
              })) : [];
              setSelectedLocations(selected);
            }}
            options={locations.map(loc => ({
              value: loc.id,
              label: loc.name
            }))}
            className="location-select-container"
            classNamePrefix="location-select"
            placeholder="Select locations..."
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: '#2196F3',
                primary75: '#2196F3cc',
                primary50: '#2196F380',
                primary25: '#2196F340',
                danger: '#f44336',
                dangerLight: '#f4433640',
                neutral0: '#011029',
                neutral5: '#011029',
                neutral10: '#011029',
                neutral20: 'rgba(255, 255, 255, 0.2)',
                neutral30: 'rgba(255, 255, 255, 0.3)',
                neutral40: 'rgba(255, 255, 255, 0.4)',
                neutral50: 'rgba(255, 255, 255, 0.5)',
                neutral60: 'rgba(255, 255, 255, 0.6)',
                neutral70: 'rgba(255, 255, 255, 0.7)',
                neutral80: 'rgba(255, 255, 255, 0.8)',
                neutral90: 'rgba(255, 255, 255, 0.9)'
              }
            })}
            styles={{
              control: (provided) => ({
                ...provided,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  borderColor: '#2196F3'
                }
              }),
              menu: (provided) => ({
                ...provided,
                backgroundColor: '#011029',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                position: 'absolute',
                zIndex: 999,
                width: '100%'
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                color: 'white',
                '&:active': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }),
              multiValue: (provided) => ({
                ...provided,
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }),
              multiValueLabel: (provided) => ({
                ...provided,
                color: 'white'
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  color: '#f44336'
                }
              }),
              input: (provided) => ({
                ...provided,
                color: 'white'
              })
            }}
          />
        </div>
      </div>

      <button 
        onClick={saveTemplate} 
        className="save-btn"
        type="button"
      >
        Save Changes
      </button>
    </div>
  );
}

export default EditTasklist;
